import type { ReactNode } from 'react'

/* Breadcrumbs and tab strip. Exports only components. */

interface Crumb {
  label: ReactNode
  /** Omit on the final crumb, or pass neither to render it as plain text. */
  href?: string
  onClick?: () => void
}

/**
 * The mockup's `Operations › ETL Explorer › Inventory Transactions` trail.
 * The last crumb always renders as current (non-interactive).
 */
export function Breadcrumbs({ items }: { items: Crumb[] }) {
  return (
    <nav className="lh-breadcrumbs" aria-label="Breadcrumb">
      {items.map((item, i) => {
        const isLast = i === items.length - 1
        return (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {i > 0 && <span className="lh-breadcrumb-sep" aria-hidden="true">›</span>}
            {isLast || (!item.href && !item.onClick) ? (
              <span className="lh-breadcrumb-current" aria-current={isLast ? 'page' : undefined}>
                {item.label}
              </span>
            ) : item.href ? (
              <a className="lh-breadcrumb-link" href={item.href}>{item.label}</a>
            ) : (
              <button type="button" className="lh-breadcrumb-link" onClick={item.onClick}>
                {item.label}
              </button>
            )}
          </span>
        )
      })}
    </nav>
  )
}

interface Tab {
  value: string
  label: ReactNode
  /** Disabled tabs stay visible so the full shape of a surface is legible. */
  disabled?: boolean
  disabledHint?: string
}

/**
 * Underline tab strip.
 *
 * Selection lives in a `data-active` attribute styled from CSS — a Mantine
 * `styles` prop would render inline and could not target it.
 */
export function TabStrip({ tabs, value, onChange }: {
  tabs: Tab[]
  value: string
  onChange: (value: string) => void
}) {
  return (
    <div className="lh-tabs" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.value}
          type="button"
          role="tab"
          className="lh-tab"
          data-active={tab.value === value ? 'true' : undefined}
          data-disabled={tab.disabled ? 'true' : undefined}
          aria-selected={tab.value === value}
          aria-disabled={tab.disabled || undefined}
          title={tab.disabled ? tab.disabledHint : undefined}
          onClick={() => { if (!tab.disabled) onChange(tab.value) }}
        >
          {tab.label}
        </button>
      ))}
    </div>
  )
}

/** A filter chip row above a table. */
export function FilterChip({ children, active, onClick }: {
  children: ReactNode
  active?: boolean
  onClick?: () => void
}) {
  return (
    <button
      type="button"
      className="lh-filter-chip"
      data-active={active ? 'true' : undefined}
      aria-pressed={active}
      onClick={onClick}
    >
      {children}
    </button>
  )
}
